import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { UserPlus, Check } from 'lucide-react';
import { usePatientRole } from '@/hooks/usePatientRole';
import { isPatientInactiveRlsError, PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';

interface CompanyCaregiver {
  userId: string;
  fullName: string | null;
  avatarUrl: string | null;
}

interface AssignCompanyCaregiverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  caregivers: CompanyCaregiver[];
  onSuccess: () => void;
}

export function AssignCompanyCaregiverDialog({
  open,
  onOpenChange,
  patientId,
  caregivers,
  onSuccess,
}: AssignCompanyCaregiverDialogProps) {
  const { t } = useTranslation();
  const { isPatientActive } = usePatientRole(patientId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAssign = async () => {
    if (!selectedId) return;
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('patient_user_links')
        .insert({
          patient_id: patientId,
          user_id: selectedId,
          role: 'caregiver' as const,
          active: true,
        });

      if (error) throw error;

      toast.success(t('patient.assignSuccess'));
      setSelectedId(null);
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error assigning caregiver:', error);
      if (isPatientInactiveRlsError(error)) {
        toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      } else {
        toast.error(t('patient.assignError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            {t('patient.assignCaregiver')}
          </DialogTitle>
          <DialogDescription>
            {t('patient.assignCaregiverDescription')}
          </DialogDescription>
        </DialogHeader>

        {caregivers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {t('patient.noCaregiverAvailable')}
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {caregivers.map((cg) => (
              <button
                key={cg.userId}
                type="button"
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  selectedId === cg.userId
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
                onClick={() => setSelectedId(cg.userId)}
              >
                <SignedAvatar
                  src={cg.avatarUrl}
                  bucket="avatars"
                  className="w-10 h-10"
                  fallback={
                    <span className="bg-muted w-full h-full flex items-center justify-center rounded-full text-sm">
                      {cg.fullName?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  }
                />
                <span className="flex-1 text-left font-medium truncate">
                  {cg.fullName || t('common.user')}
                </span>
                {selectedId === cg.userId && (
                  <Check className="w-5 h-5 text-primary" />
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedId || isSubmitting || !isPatientActive}
          >
            {isSubmitting ? t('common.processing') : t('patient.assignAction')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
